import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { reactive } from "vue";
/**
 * コミュニティトーク>トークルーム1つ毎に1つ所持されるStore。
 * リプライ投稿情報を管理する。
 */
interface IReplyPostState {
	/**
	 * リプライ投稿内容
	 */
	_contents: string;
	/**
	 * リプライ投稿メディアViewModel。
	 */
	_postMediaViewModels: PostMediaViewModel[];
	/**
	 * トークルームID。
	 */
	_talkRoomId: string;
	/**
	 * リプライ投稿プリセット内容。
	 */
	_presetContents: string | null;
	/**
	 * リプライ投稿エリア表示フラグ。
	 */
	_isDisplayReplyPostArea: boolean;
	/**
	 * 画像・動画を投稿中かどうか。
	 */
	_isMediaPostProcessing: boolean;
	/**
	 * リプライ返信ボタンを押されたかどうかフラグ。
	 */
	_isClickReplyResponse: boolean;
}
interface IUseReplyPostStore {
	/**
	 * 初期設定。
	 */
	init: (talkRoomId: string) => void;
	/**
	 * リプライ投稿内容。
	 */
	getContents: () => string;
	/**
	 * リプライ投稿内容: セット。
	 */
	setContents: (value: string) => void;
	/**
	 * リプライ投稿メディアViewModel。
	 */
	getPostMediaViewModels: () => PostMediaViewModel[];
	/**
	 * リプライ投稿メディアViewModel: セット。
	 */
	setPostMediaViewModels: (value: PostMediaViewModel[]) => void;
	/**
	 * トークルームID。
	 */
	getTalkRoomId: () => string;
	/**
	 * リプライ投稿プリセット内容。
	 */
	getPresetContents: () => string | null;
	/**
	 * リプライ投稿プリセット内容: セット。
	 */
	setPresetContents: (value: string) => void;
	/**
	 * リプライ投稿エリア表示フラグ。
	 */
	getIsDisplayReplyPostArea: () => boolean;
	/**
	 * リプライ投稿エリア表示フラグ: セット。
	 */
	setIsDisplayReplyPostArea: (value: boolean) => void;
	/**
	 * 画像・動画を投稿中かどうか。
	 */
	getIsMediaPostProcessing: () => boolean;
	/**
	 * リプライ投稿エリア表示フラグ: セット。
	 */
	setIsMediaPostProcessing: (value: boolean) => void;
	/**
	 * リプライ返信ボタンを押されたかどうかフラグ。
	 */
	getIsClickReplyResponse: () => boolean;
	/**
	 * リプライ返信ボタンを押されたかどうかフラグ: セット。
	 */
	setIsClickReplyResponse: (value: boolean) => void;
}
/**
 * 貼り付けタグで読み取る情報の状態管理。
 */
export const useReplyPostStore = (): IUseReplyPostStore => {
	// 状態
	const state = reactive<IReplyPostState>({
		_contents: "",
		_postMediaViewModels: [],
		_talkRoomId: "",
		_presetContents: null,
		_isDisplayReplyPostArea: false,
		_isMediaPostProcessing: false,
		_isClickReplyResponse: false,
	});
	const init = (talkRoomId: string): void => {
		state._talkRoomId = talkRoomId;
	};
	// #region contents
	const getContents = (): string => {
		return state._contents;
	};
	const setContents = (value: string): void => {
		state._contents = value;
	};
	// #endregion
	// #region postMediaViewModels
	const getPostMediaViewModels = (): PostMediaViewModel[] => {
		return state._postMediaViewModels;
	};
	const setPostMediaViewModels = (value: PostMediaViewModel[]): void => {
		state._postMediaViewModels = value;
	};
	// #endregion
	// #region talkRoomId
	const getTalkRoomId = (): string => {
		return state._talkRoomId;
	};
	// #endregion
	// #region presetContents
	const getPresetContents = (): string | null => {
		return state._presetContents;
	};
	const setPresetContents = (value: string): void => {
		state._presetContents = value;
		// 投稿内容にクリック元のリプライ内容を含める
		// リプライ内容が既に格納されている場合、改行を挟んで内容をappendする
		state._contents = `${state._contents ? state._contents + "\n" : state._contents}${state._presetContents}`;
	};
	// #endregion
	// #region isDisplayReplyPostArea
	const getIsDisplayReplyPostArea = (): boolean => {
		return state._isDisplayReplyPostArea;
	};
	const setIsDisplayReplyPostArea = (value: boolean): void => {
		state._isDisplayReplyPostArea = value;
	};
	// #endregion
	// #region isMediaPostProcessing
	const getIsMediaPostProcessing = (): boolean => {
		return state._isMediaPostProcessing;
	};
	const setIsMediaPostProcessing = (value: boolean): void => {
		state._isMediaPostProcessing = value;
	};
	// #endregion
	// #region isClickReplyResponse
	const getIsClickReplyResponse = (): boolean => {
		return state._isClickReplyResponse;
	};
	const setIsClickReplyResponse = (value: boolean): void => {
		state._isClickReplyResponse = value;
	};
	// #endregion
	return {
		init,
		getContents,
		setContents,
		getPostMediaViewModels,
		setPostMediaViewModels,
		getTalkRoomId,
		getPresetContents,
		setPresetContents,
		getIsDisplayReplyPostArea,
		setIsDisplayReplyPostArea,
		getIsMediaPostProcessing,
		setIsMediaPostProcessing,
		getIsClickReplyResponse,
		setIsClickReplyResponse,
	};
};
// ステートの型を登録し、外部でもIUseAnimationを参照し、型を効かせる
export type ReplyPostStore = ReturnType<typeof useReplyPostStore>;

/**
 * 対象のシンボルキーが利用可能かどうかを示す。
 * @param key シンボルキー代わりの文字列。
 * @returns `true`:利用可能 / `false`:利用不可。
 */
export const isValidReplyPostStoreKey = (key: string): boolean => {
	// なんらかの原因でInjectKeyにエラーが発生していた場合はコンソール出力
	if (!key) {
		console.error(`Injection failed.`);
		return false;
	}
	return true;
};
